import { ShallowReactive, computed, ComputedRef, shallowReactive, watch } from 'vue';
import { LocationTypeManager } from "./LocationTypeManager"
import {
    distanceInMiBetweenEarthCoordinates
} from './locationHelpers';
import {
    ILocation,
    ILocationMarker,
    ICenter,
    ILocationType,
    IExternalSource,
    IProximityResponse
} from './LocationFinderTypes';
import { Loader } from '@googlemaps/js-api-loader';
import { SearchManager } from './SearchManager';
import { CustomFilterManager } from './CustomFilterManager';
import * as _ from "lodash";
export default class LocationManager {
    private CustomFilterManager: ShallowReactive<CustomFilterManager>;
    private LocationTypeManager: ShallowReactive<LocationTypeManager>;
    private SearchManager: ShallowReactive<SearchManager>;
    private InternalLocations: ILocation[];
    private ExternalLocations: ShallowReactive<IExternalSource>;
    private DetailsUrl: string;
    private ConfigCenter: ICenter;
    private GoogleMapsLoader: Loader;
    private Geocoder?: google.maps.Geocoder;
    private MaxRadius: number;
    private IsCoopEnabled: boolean;
    private EnableDetails: boolean;
    public Center: ShallowReactive<ICenter>;
    public MapBounds?: ShallowReactive<google.maps.LatLngBounds>;
    public MapCenter: ShallowReactive<ICenter>;

    constructor(
        customFilterManager: ShallowReactive<CustomFilterManager>,
        locationTypeManager: ShallowReactive<LocationTypeManager>,
        searchManager: ShallowReactive<SearchManager>,
        locations: ILocation[],
        center: ICenter,
        detailsUrl: string,
        googleApiKey: string,
        maxRadius: number,
        isCoopEnabled: boolean,
        enableDetails: boolean
    ) {
        this.CustomFilterManager = customFilterManager;
        this.LocationTypeManager = locationTypeManager;
        this.SearchManager = searchManager;
        this.InternalLocations = locations;
        this.DetailsUrl = detailsUrl;
        this.ConfigCenter = center;
        this.Center = shallowReactive(center);
        this.MapCenter = shallowReactive(center);
        this.MaxRadius = maxRadius;
        this.IsCoopEnabled = isCoopEnabled;
        this.EnableDetails = enableDetails;

        this.ExternalLocations = shallowReactive(
            {
                ATM: [],
                SharedBranch: []
            })

        this.SubscribeToSearchChanges();

        this.GoogleMapsLoader = new Loader({
            apiKey: googleApiKey,
            version: 'weekly',
            libraries: ['places'],
        });

        this.InitGeocoder();
    }

    public GetVisibleLocations(): ComputedRef<ILocationMarker[]> {
        return computed(() => {
            let vLocations: ILocationMarker[] = []

            const fLocations = this.FilterLocationsBySelectedLocationTypes(this.InternalLocations)

            vLocations = this.MapLocationsWithPinIcon(fLocations)

            vLocations = this.FilterLocationsByCustomFilter(vLocations)

            vLocations = this.FilterLocationsBySearchParam(vLocations);

            //vLocations = this.AddExternalLocations(vLocations)

            vLocations = this.FilterByItsDistanceToCenter(vLocations)

            //vLocations = this.SortByItsDistanceToCenter(vLocations)
            vLocations = this.SortByCityAlphabetically(vLocations)
            return vLocations
        })
    }

    public async GetDataFromApi(externalCenter: ICenter | null = null): Promise<ICenter | undefined> {
        const center = await this.GetPositionFromSearchValue();
        const requestCenter = externalCenter || center;

        if (!requestCenter) {
            console.log("Invalid search")
        } else {
            if (this.LocationTypeManager.IsSharedBranchSelected()) {
                this.GetLocationsFromAPI("S", requestCenter)
            } else {
                this.ExternalLocations.SharedBranch = []
            };
            if (this.LocationTypeManager.IsAtmSelected()) {
                this.GetLocationsFromAPI("A", requestCenter)
            } else {
                this.ExternalLocations.ATM = []
            };
        }
        return requestCenter
    }

    public FitMapToBounds(bounds : google.maps.LatLngBounds) {
        this.MapBounds = bounds;
    }

    public FitMapToLocations(locations: ILocation[]) {
        const bounds = new google.maps.LatLngBounds();
        locations.forEach(loc => bounds.extend(new google.maps.LatLng(loc.Latitude as number, loc.Longitude as number)));
        this.MapBounds = bounds;
    }

    private async GetPositionFromSearchValue(): Promise<ICenter | undefined> {
        let searchAddress = ""
        const searchValue = this.SearchManager.Search;

        if (/(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)/.test(searchValue)) {
            searchAddress = `USA ${searchValue}`;
        } else {
            searchAddress = searchValue
        }
 
        if (searchAddress == "") {
            return this.Center
        }

        const res = await this.Geocoder?.geocode({ address: searchValue })
            .then((res) => {
                const lat = res.results[0].geometry.location.lat();
                const lng = res.results[0].geometry.location.lng();
                const center: ICenter = { lat, lng }
                return center
            })
            .catch(err => {
                return this.ConfigCenter
            })

        return res
    }

    private InitGeocoder() {
        this.GoogleMapsLoader
            .load()
            .then(() => {
                return new google.maps.Geocoder();
            })
            .then((gc) => {
                this.Geocoder = gc;
            })
            .catch((e) => {
                console.error(e);
            });
    }

    private SubscribeToSearchChanges() {
        watch(this.SearchManager, (manager) => {
            const isSearchDifferent = manager.Search != "";

          if (this.LocationTypeManager.IsAnyExternalSourceSelected() && isSearchDifferent) {
            this.GetDataFromApi()
              .then(center => this.Center = center as ICenter)
              .catch(err => console.error("Error finding new center: ", err))
          } else if (isSearchDifferent) {
            this.GetPositionFromSearchValue()
              .then(center => { if (center) this.Center = center as ICenter })
              .catch(err => console.error("Error finding new center: ", err))
          } 
        })
    }

    private FilterLocationsBySearchParam(locations: ILocationMarker[]): ILocationMarker[] {
        const lowerCaseSearch = this.SearchManager.Search.toLowerCase();

        if (this.LocationTypeManager.IsAnyExternalSourceSelected() || lowerCaseSearch == "" ) return locations;
        return locations.filter((loc) => {
                return (
                    loc.Title?.toLowerCase().includes(lowerCaseSearch) ||
                    loc.DisplayName?.toLowerCase().includes(lowerCaseSearch) ||
                    loc.City.toLowerCase().includes(lowerCaseSearch) ||
                    loc.State.toLowerCase().includes(lowerCaseSearch) ||
                    loc.Zip.toLowerCase().includes(lowerCaseSearch)
                );
            });
    }

    private FilterByItsDistanceToCenter(locations: ILocationMarker[]): ILocationMarker[] {
        //if (!this.IsCoopEnabled) return locations;

      const isSearchEmpty = this.SearchManager.Search == "";

      if (isSearchEmpty) {
        return locations.filter(elm => distanceInMiBetweenEarthCoordinates(elm.Latitude as number, elm.Longitude as number, this.Center) < (this.MaxRadius * 2))
      }

      return locations
    }

    private SortByItsDistanceToCenter(locations: ILocationMarker[]): ILocationMarker[] {
        return locations.sort((a, b) => a.Distance - b.Distance);
    }

    private SortByCityAlphabetically(locations: ILocationMarker[]): ILocationMarker[] {
      return locations.sort((a, b) =>
          a.DisplayName.localeCompare(b.DisplayName)
        );
    }

    private AddExternalLocations(locations: ILocationMarker[]): ILocationMarker[] {
        const ATM = this.LocationTypeManager.IsAtmSelected() ? this.ExternalLocations.ATM : [];
        const SB = this.LocationTypeManager.IsSharedBranchSelected() ? this.ExternalLocations.SharedBranch : [];

        if (this.LocationTypeManager.IsAnyExternalSourceSelected()) {
            return locations.concat(ATM, SB)
        }

        return locations
    }

    private FilterLocationsByCustomFilter(locations: ILocationMarker[]): ILocationMarker[] {
        if (this.CustomFilterManager.IsAnyFilterSelected()) {
            return locations.filter(elm => elm.CustomFilters && this.CustomFilterManager.SelectedFiltersId().filter(filterId => elm.CustomFilters?.map(item => item.Id).includes(filterId)).length > 0)
        }

        return locations
    }

    private FilterLocationsBySelectedLocationTypes(locations: ILocation[]): ILocation[] {
        const selectedLocationTypesId = this.LocationTypeManager.InternalSource.filter(elm => elm.Selected).map(item => item.Id);
        return locations.filter(item => selectedLocationTypesId.includes((item.LocationType as ILocationType).Id))
    }

    private MapLocationsWithPinIcon(locations: ILocation[]): ILocationMarker[] {
        const counter = this.LocationTypeManager.Counter;

        return locations.map((loc) => {
            const locationType = this.LocationTypeManager.GetInternalLocationTypeFromId((loc.LocationType as ILocationType).Id) as ILocationType;
            const urlTemp = this.EnableDetails ? `${this.DetailsUrl}/location/${loc.Id}` : undefined;
            return ({
                ...loc,
                Id: `${loc.Id}-${counter}`,
                Url: urlTemp,
                Distance: distanceInMiBetweenEarthCoordinates(
                    loc.Latitude as number,
                    loc.Longitude as number,
                    this.ConfigCenter),
                PinIcon: locationType.ImageUrl,
                PinBackground: locationType.BackgroundColor,
                PinBorder: locationType.TextColor,
                Base64PinIcon: locationType.Base64Icon
            })
        })
    }

    private async RequestLocationsToApi(locatorType: string, center: ICenter): Promise<Record<string, IProximityResponse>> {
        return fetch('/get/sharedBranch', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ...center,
                locatorType,
            })
        })
            .then(res => res.json())
    }

    private GetIconBackgroundAndBorder(locatorType: string) {
        const sharedBranch = this.LocationTypeManager.ExternalSharedBranch
        const atm = this.LocationTypeManager.ExternalAtm
        const sbIcon = sharedBranch?.Base64Icon;
        const atmIcon = atm?.Base64Icon

        if (locatorType == "S" && typeof sharedBranch != 'undefined') {
            return {
                pinIcon: sharedBranch.ImageUrl,
                base64Icon: sbIcon,
                pinBackground: sharedBranch.BackgroundColor,
                pinBorder: sharedBranch.TextColor
            }
        } else if (locatorType == "A" && typeof atm != 'undefined') {
            return {
                pinIcon: atm.ImageUrl,
                base64Icon: atmIcon,
                pinBackground: atm.BackgroundColor,
                pinBorder: atm.TextColor

            }
        } else {
            return {
                pinIcon: null,
                base64Icon: null,
                pinBackground: "currentColor",
                pinBorder: "currentColor"

            }
        }
    }

    private MapInnerSharedBranchWithPinIcon({ pinIcon, base64Icon, pinBackground, pinBorder }: any): ILocationMarker[] {
        const sharedBranch = this.LocationTypeManager.ExternalSharedBranch
        let innerSB: ILocationMarker[] = [];

        if (typeof sharedBranch != 'undefined') {
            innerSB = this.InternalLocations
                .filter(elm => elm.LocationType?.Id == sharedBranch.Id)
                .map((loc) => ({
                    ...loc,
                    Distance: distanceInMiBetweenEarthCoordinates(
                        loc.Latitude as number,
                        loc.Longitude as number,
                        this.ConfigCenter
                    ),
                    PinIcon: pinIcon,
                    PinBackground: pinBackground,
                    Base64PinIcon: base64Icon,
                    PinBorder: pinBorder

                })) as ILocationMarker[];
        }
        return innerSB
    }

    private MapInnerAtmWithPinIcon({ pinIcon, base64Icon, pinBackground, pinBorder }: any): ILocationMarker[] {

        const atm = this.LocationTypeManager.ExternalAtm
        let innerATM: ILocationMarker[] = [];

        if (typeof atm != 'undefined') {
            innerATM = this.InternalLocations
                .filter(elm => elm.LocationType?.Id == atm.Id)
                .map((loc) => ({
                    ...loc,
                    Distance: distanceInMiBetweenEarthCoordinates(
                        loc.Latitude as number,
                        loc.Longitude as number,
                        this.ConfigCenter
                    ),
                    PinIcon: pinIcon,
                    PinBackground: pinBackground,
                    Base64PinIcon: base64Icon,
                    PinBorder: pinBorder
                })) as ILocationMarker[];
        }
        return innerATM;
    }

    private async GetLocationsFromAPI(locatorType: string, center: ICenter): Promise<ILocationMarker[]> {
        const res = await this.RequestLocationsToApi(locatorType, center);

        const { pinIcon, base64Icon, pinBackground, pinBorder } = this.GetIconBackgroundAndBorder(locatorType)

        const maped: ILocationMarker[] = res.data.Locations
            .map((elm) => {
                return {
                    ...elm,
                    DisplayName: elm.InstitutionName,
                    Latitude: parseFloat(elm.Latitude as string),
                    Longitude: parseFloat(elm.Longitude as string),
                    Distance: distanceInMiBetweenEarthCoordinates(
                        parseFloat(elm.Latitude as string), parseFloat(elm.Longitude as string), this.ConfigCenter
                    ),
                    PinBackground: pinBackground,
                    PinIcon: pinIcon,
                    Base64PinIcon: base64Icon,
                    PinBorder: pinBorder
                }
            })

        const innerSB = this.MapInnerSharedBranchWithPinIcon({ pinIcon, base64Icon, pinBackground, pinBorder });
        const innerATM = this.MapInnerAtmWithPinIcon({ pinIcon, base64Icon, pinBackground, pinBorder });

        if (locatorType == "S") {
            const newLocations = maped.concat(innerSB, this.ExternalLocations.SharedBranch);
            this.ExternalLocations.SharedBranch = _.uniqBy(newLocations, 'Id');
        }

        if (locatorType == "A") {
            const newLocations = maped.concat(innerATM, this.ExternalLocations.ATM);
            this.ExternalLocations.ATM = _.uniqBy(newLocations, 'Id');
        }

        return maped;
    }
}
 
